/**
 * B-16 (SOPZ G4): silnik personalizacji UI panelu.
 *
 * Jedno miejsce, które przekłada preferencje użytkownika na DOM:
 *  - motyw jasny/ciemny/systemowy (klasa `.dark` na <html>),
 *  - tryb wysokiego kontrastu (klasa `.high-contrast`, WCAG),
 *  - akcent kolorystyczny (nadpisanie zmiennych CSS `--primary`/`--ring`),
 *  - gęstość interfejsu (atrybut `data-density`).
 *
 * Źródłem prawdy jest `User.uiPreferences` (baza, idzie za kontem); `ui.store`
 * trzyma runtime + cache w localStorage (mitygacja FOUC), a to jest warstwa
 * zastosowania. Powiadomienia (kanały + wyciszone typy) egzekwuje backend.
 */

export type ThemeMode = 'light' | 'dark' | 'system';
export type Density = 'comfortable' | 'compact';
export type AccentName = 'blue' | 'green' | 'violet' | 'amber' | 'teal' | 'rose';

export interface NotificationPrefs {
  push: boolean;
  inApp: boolean;
  email: boolean;
  /** Wyciszone typy powiadomień (NotificationType) — nie przychodzą wcale. */
  mutedTypes: string[];
}

export interface UiPreferences {
  theme: ThemeMode;
  accent: AccentName;
  highContrast: boolean;
  density: Density;
  notifications: NotificationPrefs;
}

export const DEFAULT_PREFERENCES: UiPreferences = {
  theme: 'system',
  accent: 'blue',
  highContrast: false,
  density: 'comfortable',
  notifications: { push: true, inApp: true, email: true, mutedTypes: [] },
};

/** Uzupełnia surowy (częściowy / null) obiekt preferencji o wartości domyślne. */
export function mergePreferences(raw: Partial<UiPreferences> | null | undefined): UiPreferences {
  const n = raw?.notifications;
  return {
    theme: raw?.theme ?? DEFAULT_PREFERENCES.theme,
    accent: raw?.accent ?? DEFAULT_PREFERENCES.accent,
    highContrast: raw?.highContrast ?? DEFAULT_PREFERENCES.highContrast,
    density: raw?.density ?? DEFAULT_PREFERENCES.density,
    notifications: {
      push: n?.push ?? true,
      inApp: n?.inApp ?? true,
      email: n?.email ?? true,
      mutedTypes: Array.isArray(n?.mutedTypes) ? n!.mutedTypes : [],
    },
  };
}

/**
 * Presety akcentu → HSL dla `--primary` (i `--ring`). `blue` = domyślny z
 * index.css, więc go NIE nadpisujemy (czyścimy inline → wraca wartość z :root/.dark).
 * primary-foreground pozostaje biały, więc kontrast tekstu na akcencie trzymany
 * z zapasem AA (jasność ≤ ~52%).
 */
export const ACCENTS: Record<AccentName, { label: string; primary: string; hex: string }> = {
  blue:   { label: 'Niebieski', primary: '207 76% 52%', hex: '#2b8fe0' },
  green:  { label: 'Zielony',   primary: '142 60% 36%', hex: '#249150' },
  violet: { label: 'Fioletowy', primary: '262 58% 55%', hex: '#7c53d6' },
  amber:  { label: 'Bursztyn',  primary: '32 85% 45%',  hex: '#d67d12' },
  teal:   { label: 'Morski',    primary: '186 62% 37%', hex: '#249aa8' },
  rose:   { label: 'Różowy',    primary: '340 68% 50%', hex: '#d63a72' },
};

export const ACCENT_NAMES = Object.keys(ACCENTS) as AccentName[];

interface Applied {
  theme: ThemeMode;
  accent: AccentName;
  highContrast: boolean;
  density: Density;
}

let systemMql: MediaQueryList | null = null;
let systemListener: ((e: MediaQueryListEvent) => void) | null = null;

function prefersDark(): boolean {
  return typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-color-scheme: dark)').matches;
}

function resolveDark(theme: ThemeMode): boolean {
  if (theme === 'dark') return true;
  if (theme === 'light') return false;
  return prefersDark();
}

/**
 * Stosuje motyw do <html>. Zwraca rozwiązane `dark` (do ikony w Topbarze).
 * `onSystemChange` odpala się, gdy w trybie `system` zmieni się motyw OS.
 */
export function applyTheme(p: Applied, onSystemChange?: (dark: boolean) => void): boolean {
  if (typeof document === 'undefined') return false;
  const root = document.documentElement;

  const dark = resolveDark(p.theme);
  root.classList.toggle('dark', dark);
  root.classList.toggle('high-contrast', !!p.highContrast);
  root.dataset.density = p.density === 'compact' ? 'compact' : 'comfortable';

  const accent = ACCENTS[p.accent] ?? ACCENTS.blue;
  if (p.accent === 'blue') {
    root.style.removeProperty('--primary');
    root.style.removeProperty('--ring');
  } else {
    root.style.setProperty('--primary', accent.primary);
    root.style.setProperty('--ring', accent.primary);
  }

  // Nasłuch zmian motywu OS tylko w trybie 'system' (odpina się przy zmianie trybu).
  if (systemMql && systemListener) {
    systemMql.removeEventListener('change', systemListener);
    systemMql = null;
    systemListener = null;
  }
  if (p.theme === 'system' && typeof window !== 'undefined' && window.matchMedia) {
    systemMql = window.matchMedia('(prefers-color-scheme: dark)');
    systemListener = (e) => {
      document.documentElement.classList.toggle('dark', e.matches);
      onSystemChange?.(e.matches);
    };
    systemMql.addEventListener('change', systemListener);
  }

  return dark;
}
